import { BuildingOffice, Icon, User } from '@phosphor-icons/react';
import { useEffect, useMemo } from 'react';
import styles from './PlanTabs.module.scss';
import classNames from 'classnames';
import { Button } from 'src/v2/commonComponents/Button';
import {
  useGetUser,
  useSubscriptionInfo,
  usePaymentControlActions,
  usePaymentControlState,
} from 'src/hooks';
import { SVG_SIZE_M, PAYMENT_SUBSYSTEM_V3_VARIANT } from 'src/constants';
import { PaymentTabs } from 'src/types';
import { useFeatureFlagVariantKey } from 'posthog-js/react';

const TAB_LIST: {
  tab: PaymentTabs;
  Icon: Icon;
  title: string;
  supportsMultipleSeats: boolean;
}[] = [
  {
    tab: PaymentTabs.INDIVIDUAL,
    Icon: User,
    title: 'Individual plans',
    supportsMultipleSeats: false,
  },
  {
    tab: PaymentTabs.BUSINESS,
    Icon: BuildingOffice,
    title: 'Business plans',
    supportsMultipleSeats: true,
  },
];

const TAB_LIST_V3: {
  tab: PaymentTabs;
  Icon: Icon;
  title: string;
  supportsMultipleSeats: boolean;
}[] = [
  {
    tab: PaymentTabs.INDIVIDUAL,
    Icon: User,
    title: 'Individual',
    supportsMultipleSeats: false,
  },
  {
    tab: PaymentTabs.BUSINESS,
    Icon: BuildingOffice,
    title: 'Business',
    supportsMultipleSeats: true,
  },
];

export const PlanTabs = () => {
  const { subscriptionInfo, isSubscriptionInfoLoading } = useSubscriptionInfo();
  const { isTeamsTier } = useGetUser();
  const { onSetSelectedPaymentTab } = usePaymentControlActions();
  const { selectedPaymentTab } = usePaymentControlState();
  const paymentSubsystemVariant = useFeatureFlagVariantKey('payment-subsystem');
  const isShowNewPaymentModalV3 =
    paymentSubsystemVariant === PAYMENT_SUBSYSTEM_V3_VARIANT;

  useEffect(() => {
    if (!isTeamsTier) {
      return;
    }

    onSetSelectedPaymentTab(PaymentTabs.BUSINESS);
  }, [isTeamsTier, onSetSelectedPaymentTab]);

  const tabsByVersion = isShowNewPaymentModalV3 ? TAB_LIST_V3 : TAB_LIST;

  const handleTabClick = ({ target }: React.MouseEvent<HTMLButtonElement>) => {
    const name = (target as HTMLButtonElement).name;
    onSetSelectedPaymentTab(name as PaymentTabs);
  };

  const tabsShown = useMemo(() => {
    if (isSubscriptionInfoLoading) {
      return [];
    }

    if (subscriptionInfo && subscriptionInfo?.supports_multiple_seats) {
      return tabsByVersion.filter(
        ({ supportsMultipleSeats }) => supportsMultipleSeats,
      );
    }

    return tabsByVersion;
  }, [isSubscriptionInfoLoading, subscriptionInfo, tabsByVersion]);

  return (
    <div className={styles.root}>
      {tabsShown.map(({ tab, Icon, title }) => {
        return (
          <Button
            color="transparent"
            key={tab}
            className={classNames(styles.tab, {
              [styles.active]: selectedPaymentTab === tab,
            })}
            name={tab}
            onClick={handleTabClick}
            disabled={isTeamsTier && tab === PaymentTabs.INDIVIDUAL}
          >
            <Icon size={SVG_SIZE_M} />
            {title}
          </Button>
        );
      })}
    </div>
  );
};
